import { useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { springs } from "@/components/ui/motion";
import { useTranslation } from "@/packages/utils";
import type {
  SeverityType,
  FindingStatusType,
} from "@/packages/domains/findings";
import {
  useSeverityConfig,
  useStatusConfig,
  useSeverityOptions,
  useStatusOptions,
} from "./findings-constants";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FindingsFilterBarProps {
  statusFilter: FindingStatusType | "all";
  severityFilter: SeverityType | "all";
  searchText: string;
  onStatusChange: (status: FindingStatusType | "all") => void;
  onSeverityChange: (severity: SeverityType | "all") => void;
  onSearchChange: (text: string) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FindingsFilterBar({
  statusFilter,
  severityFilter,
  searchText,
  onStatusChange,
  onSeverityChange,
  onSearchChange,
}: FindingsFilterBarProps) {
  const { t } = useTranslation("audits");
  const [searchFocused, setSearchFocused] = useState(false);
  const severityConfig = useSeverityConfig();
  const statusConfig = useStatusConfig();
  const severityOptions = useSeverityOptions();
  const statusOptions = useStatusOptions();
  const hasActiveFilters = statusFilter !== "all" || severityFilter !== "all";

  function handleClearAll() {
    onStatusChange("all");
    onSeverityChange("all");
    onSearchChange("");
  }

  return (
    <motion.div
      className="flex items-center gap-1.5 mb-4"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.snappy, delay: 0.05 }}
    >
      {/* "All" chip */}
      <button
        type="button"
        onClick={handleClearAll}
        className={`relative rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer ${
          !hasActiveFilters
            ? "text-accent"
            : "text-secondary hover:text-primary"
        }`}
      >
        {!hasActiveFilters && (
          <motion.div
            layoutId="findings-filter-all"
            className="absolute inset-0 rounded-full bg-accent/15"
            transition={springs.snappy}
          />
        )}
        <span className="relative z-10">{t("findings.filters.all")}</span>
      </button>

      {/* Status chips */}
      {statusOptions.map((opt) => {
        const isActive = statusFilter === opt.value;
        const style = statusConfig[opt.value] ?? statusConfig.open;

        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onStatusChange(isActive ? "all" : opt.value)}
            className={`relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer ${
              isActive ? "text-primary" : "text-secondary hover:text-primary"
            }`}
          >
            {isActive && (
              <motion.div
                layoutId={`findings-status-${opt.value}`}
                className="absolute inset-0 rounded-full"
                style={{
                  backgroundColor: style.bg,
                }}
                transition={springs.snappy}
              />
            )}
            <span
              className="relative z-10 h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: style.text }}
            />
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}

      {/* Vertical divider */}
      <div className="mx-1 h-4 w-px bg-border" />

      {/* Severity chips */}
      {severityOptions.map((opt) => {
        const isActive = severityFilter === opt.value;
        const config = severityConfig[opt.value];

        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onSeverityChange(isActive ? "all" : opt.value)}
            className={`relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer ${
              isActive ? "text-primary" : "text-secondary hover:text-primary"
            }`}
          >
            {isActive && (
              <motion.div
                layoutId={`findings-severity-${opt.value}`}
                className="absolute inset-0 rounded-full"
                style={{
                  backgroundColor: config.bg,
                }}
                transition={springs.snappy}
              />
            )}
            <span
              className="relative z-10 h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: config.dot }}
            />
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Inline search input */}
      <motion.div
        className="flex items-center rounded-full border bg-surface overflow-hidden"
        animate={{
          borderColor: searchFocused ? "#E8734A" : "var(--color-border)",
          boxShadow: searchFocused
            ? "0 0 0 2px rgba(232,115,74,0.1)"
            : "0 0 0 0px rgba(232,115,74,0)",
          width: searchFocused || searchText ? 200 : 32,
        }}
        transition={springs.snappy}
      >
        <MagnifyingGlassIcon
          className="mx-2 h-3.5 w-3.5 shrink-0 text-muted cursor-pointer"
          weight="bold"
          onClick={() => setSearchFocused(true)}
        />
        <input
          type="text"
          placeholder={t("findings.searchPlaceholder")}
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="h-7 w-full bg-transparent pr-2 text-[12px] text-primary placeholder:text-muted outline-none"
        />
      </motion.div>
    </motion.div>
  );
}
